import React, {useContext} from 'react';

import {LocaleSwitcher} from '@homeaway/react-locale-switcher';
import {TrackingContext} from '@homeaway/react-event-tracking';

import CurrencySelectorConnector from '../../connectors/CurrencySelectorConnector';

import {Flags} from '../flags/Flags';
import {Help} from '../help/Help';
import {Inbox} from '../inbox/Inbox';
import {Login} from '../login/Login';
import {Roadtrips} from '../roadtrips/Roadtrips';
import {Scratchpad} from '../scratchpad/Scratchpad';
import {SignUp} from '../signup/SignUp';
import {SupportNumberScreenreader} from '../support-number-screenreader/SupportNumberScreenreader';
import {Traveler} from '../traveler/Traveler';
import {triggerEdapEvent, edapTrigger} from '../../utils/triggerEdap';
import {linkLocations, namedEvents} from '../../utils/edap';

import './Nav.less';

function Nav({
    accessibility,
    currencyCode,
    extraNav,
    feedbackComponent,
    flags,
    globalizationSelector,
    help,
    inbox,
    internalTools,
    isMobile,
    localeSwitcher,
    login,
    onGlobalSelectorChange,
    renderCurrencySelector,
    roadTrips,
    scratchpad,
    signUp,
    traveler,
    tripboard
}) {
    // Add the context to be used for pushing edap events
    const context = useContext(TrackingContext);

    function handleClickInbox() {
        triggerEdapEvent(context, linkLocations.HEADER_LINK_INBOX);
        edapTrigger(namedEvents.INBOX_SELECTED, {action_location: linkLocations.HEADER});
    }

    function handleClickScratchPad() {
        triggerEdapEvent(context, linkLocations.HEADER_LINK_TRIPBOARDS);
        edapTrigger(namedEvents.CTA_SELECTED, {action_location: linkLocations.HEADER_LINK_TRIPBOARDS, event_action: namedEvents.CTA_HEADER_TAB_CLICKED});
    }

    function handleClickRoadTrips() {
        const {onClickRoadTrips} = roadTrips;

        triggerEdapEvent(context, linkLocations.HEADER_LINK_ROADTRIPS);

        if (typeof onClickRoadTrips === 'function') {
            onClickRoadTrips();
        }
    }

    function handleFeedbackClick() {
        triggerEdapEvent(context, linkLocations.HEADER_LINK_FEEDBACK);
    }

    function renderAccessibility() {
        if (!accessibility) {
            return null;
        }
        return (
            <SupportNumberScreenreader {...accessibility} />
        );
    }

    function renderScratchpad() {
        if (!scratchpad) {
            return null;
        }

        return (
            <a
                className="site-header-nav__link"
                data-bypass="true"
                href={scratchpad.link}
                onClick={handleClickScratchPad}
                rel="nofollow"
            >
                <Scratchpad {...{...scratchpad, tripboard}}/>
            </a>
        );
    }

    function renderRoadTrips() {
        if (!roadTrips) {
            return null;
        }

        const {badgeLabel, label, link, showBadgeLabel} = roadTrips;
        if (!label || !link) {
            return null;
        }

        return (
            <a
                className="site-header-nav__link"
                data-bypass="true"
                href={link}
                onClick={handleClickRoadTrips}
                rel="nofollow"
            >
                <Roadtrips
                    badgeLabel={badgeLabel}
                    isWithNav
                    label={label}
                    showBadgeLabel={showBadgeLabel}
                />
            </a>
        );
    }

    function renderInbox() {
        if (!traveler || !inbox) {
            return null;
        }

        return (
            <a className="site-header-nav__link" data-bypass="true" href={inbox.link} onClick={handleClickInbox}>
                <Inbox {...inbox} />
            </a>
        );
    }

    function renderLogin() {
        if (!login) {
            return null;
        }

        return (
            <Login {...login} />
        );
    }

    function renderSignUp() {
        if (signUp) {
            return <SignUp {...login} {...signUp}/>;
        }
        return null;
    }

    function renderTraveler() {
        if (!traveler) {
            return null;
        }

        return (
            <Traveler {...traveler} />
        );
    }

    function renderHelp() {
        if (!help) {
            return null;
        }

        return (
            <Help {...help} />
        );
    }

    function renderExtraNav() {
        if (!extraNav) {
            return null;
        }

        if (!extraNav.items) {
            return null;
        }

        return extraNav.items.map((item, index) => (
            <a
                className={`site-header-nav__link ${item.cssClass}`}
                data-bypass="true"
                href={item.link}
                key={index}
            >{item.text}</a>
        ));
    }

    function renderInternalTools() {
        if (!internalTools) {
            return null;
        }

        return internalTools;
    }

    function renderFeedbackComponent() {
        if (isMobile || !feedbackComponent) {
            return null;
        }

        return (
            <div className="site-header-feedback" onClick={handleFeedbackClick}>
                {feedbackComponent}
            </div>
        );
    }

    function renderFlags() {
        if (!flags) {
            return null;
        }

        return (
            <Flags {...flags} />
        );
    }

    function renderCurrencySwitcher() {
        if (!renderCurrencySelector) {
            return null;
        }

        return (
            <CurrencySelectorConnector
                className="site-header-nav__dropdown"
                {...globalizationSelector}
                currencyCode={currencyCode}
                isMobile={isMobile}
                onCurrencySelectorChange={onGlobalSelectorChange}
                renderCurrencySelector={renderCurrencySelector}
            />
        );
    }


    function renderLocaleSwitcher() {
        if (!localeSwitcher || !localeSwitcher.locales || localeSwitcher.locales.length <= 1) {
            return null;
        }

        return (
            <div className="site-header-nav__dropdown">
                <LocaleSwitcher
                    locales={localeSwitcher.locales}
                    translations={localeSwitcher.translations}
                />
            </div>
        );
    }

    return (
        <div className="site-header-nav" role="navigation">
            {renderAccessibility()}
            {renderRoadTrips()}
            {renderScratchpad()}
            {renderInbox()}
            {renderLogin()}
            {renderSignUp()}
            {renderTraveler()}
            {renderHelp()}
            {renderExtraNav()}
            {renderInternalTools()}
            {renderFeedbackComponent()}
            <div className="currency-selector-container site-header-nav__dropdown">
                {renderCurrencySwitcher()}
            </div>
            {renderFlags()}
            {renderLocaleSwitcher()}
        </div>
    );
}

export {Nav};
